"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { directPlatformBase, platformFetch, platformSend } from "./browser";
import { PlatformError } from "./client";
import { authorizeUrl, isFromAuthWindow, originOf, popupFeatures, type OAuthProvider } from "./oauth";
import { clearReferralCode, signInWithReferral, storedReferral, type Referral } from "./referral-intake";
import { authWindowMessage, refusalFromAuthWindow, type SignInRefusal } from "./signin-outcome";
import { telegramWidgetFromStamp } from "./signin-methods";
import type { TelegramLoginUser } from "@/lib/telegram/types";

/* Sign-in by Google, X and Telegram, and the Telegram link on the account
   page — the whitelabel's set (batman's RegisterModal, TelegramWebAppProvider
   and LinkedAccounts), on this app's own client. */

type SignedIn = { data: { id: string; nickname: string } };

/* The API base the popups open on. Read after mount, because the server
   render has no <html data-platform-api> to read and the two passes must
   agree. */
export const useDirectPlatformBase = () => {
  const [base, setBase] = useState<string | null>(null);

  useEffect(() => {
    setBase(directPlatformBase());
  }, []);

  return base;
};

/* Whether the Telegram Login Widget runs on this host (signin-methods.ts).
   False until mounted, so a refused host never flashes the button. */
export const useTelegramWidgetAllowed = () => {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    setAllowed(telegramWidgetFromStamp(document.documentElement.dataset.telegramWidget));
  }, []);

  return allowed;
};

export type OAuthState =
  | { status: "idle" }
  | { status: "pending" }
  | { status: "signed-in" }
  | { status: "refused"; refusal: SignInRefusal };

/* One provider's popup sign-in. `start` must be called from the click itself:
   the popup is opened synchronously, or the browser blocks it. */
export const useOAuthSignIn = (provider: OAuthProvider, onSignedIn?: () => void) => {
  const client = useQueryClient();
  const base = useDirectPlatformBase();
  const [state, setState] = useState<OAuthState>({ status: "idle" });
  const popup = useRef<Window | null>(null);
  /* The referral the open popup was sent with — null once it has been
     dropped, which is what stops a second conflict from looping. */
  const sentReferral = useRef<Referral | null>(null);
  const signedIn = useRef(onSignedIn);
  signedIn.current = onSignedIn;

  useEffect(() => {
    const apiOrigin = base ? originOf(base) : null;

    if (!base || !apiOrigin) {
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (!isFromAuthWindow(event, { apiOrigin, popup: popup.current })) {
        return;
      }

      const message = authWindowMessage(event.data);

      if (!message) {
        return;
      }

      if (message.type === "REFERRAL_CODE_CONFLICT") {
        /* The click id is someone else's. The popup was told to stay open, so
           it is sent round again without the referral — once. */
        clearReferralCode();
        const opened = popup.current;

        if (sentReferral.current !== null && opened && !opened.closed) {
          sentReferral.current = null;
          opened.location.replace(authorizeUrl(base, provider, { origin: location.origin, referral: null }));
          return;
        }

        opened?.close();
        popup.current = null;
        setState({ status: "refused", refusal: { kind: "interrupted" } });
        return;
      }

      /* Every other message closes the window from its own side. */
      popup.current = null;

      if (message.type === "SIGNIN_COMPLETE") {
        clearReferralCode();
        setState({ status: "signed-in" });
        client.resetQueries();
        signedIn.current?.();
        return;
      }

      setState({ status: "refused", refusal: refusalFromAuthWindow(message) ?? { kind: "failed" } });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [base, provider, client]);

  /* A popup the player closed sends nothing. Back to idle, so the button is
     usable again — but the listener stays, because a provider page that cuts
     the window off from its opener (Cross-Origin-Opener-Policy) can make a
     live popup read as closed, and its message must still count if it comes. */
  useEffect(() => {
    if (state.status !== "pending") {
      return;
    }

    const timer = window.setInterval(() => {
      if (!popup.current || popup.current.closed) {
        window.clearInterval(timer);
        setState((current) => (current.status === "pending" ? { status: "idle" } : current));
      }
    }, 700);

    return () => window.clearInterval(timer);
  }, [state.status]);

  const start = useCallback(() => {
    if (!base) {
      return;
    }

    const referral = storedReferral();
    sentReferral.current = referral;
    const url = authorizeUrl(base, provider, { origin: location.origin, referral });
    const opened =
      window.open(url, `signin-${provider}`, popupFeatures(window)) ?? window.open(url, "_blank");

    if (!opened) {
      setState({ status: "refused", refusal: { kind: "interrupted" } });
      return;
    }

    popup.current = opened;
    setState({ status: "pending" });
  }, [base, provider]);

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { state, start, reset, available: base !== null };
};

/* The Login Widget's user, verified by the platform. */
export const useTelegramSignIn = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (user: TelegramLoginUser) =>
      signInWithReferral(
        async (referral) =>
          (await platformSend<SignedIn>("signin/telegram/verify", { ...user, ...referral })).data,
      ),
    onSuccess: () => {
      client.resetQueries();
    },
  });
};

/* Inside Telegram: the Mini App's initData, verified by the platform. Called
   once per page load by TelegramMiniApp. */
export const useTelegramMiniAppSignIn = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (initData: string) =>
      signInWithReferral(
        async (referral) =>
          (await platformSend<SignedIn>("signin/telegram/mini-app", { init_data: initData, ...referral })).data,
      ),
    onSuccess: () => {
      client.resetQueries();
    },
  });
};

export type Authenticator = {
  type: "siwe" | "siws" | "magic-link" | "telegram" | "google" | "twitter";
  displayValue: string | null;
};

/* The ways the signed-in account can sign in, as the platform lists them. */
export const useAuthenticators = (enabled: boolean) =>
  useQuery({
    queryKey: ["authenticators"],
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await platformFetch<{ data: Authenticator[] }>("profile/authenticators")).data,
  });

/* Telegram is the one provider an existing account can link. */
export const useLinkTelegram = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (user: TelegramLoginUser) =>
      (await platformSend<{ data: Authenticator }>("profile/authenticators/telegram", user)).data,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["authenticators"] });
    },
  });
};

export const linkTelegramFailure = (error: unknown) => {
  if (error instanceof PlatformError && error.status === 409) {
    const code = (error.details as { code?: unknown } | null)?.code;

    if (code === "LINKED_TO_ANOTHER_ACCOUNT") {
      return "That Telegram account already belongs to another account here.";
    }

    if (code === "TELEGRAM_ALREADY_LINKED") {
      return "This account already has a Telegram account linked.";
    }
  }

  if (error instanceof PlatformError && error.status === 401) {
    return "Telegram's confirmation was not accepted. Try again.";
  }

  if (error instanceof PlatformError && error.status === 403) {
    return "Telegram is not set up for this site.";
  }

  return "Could not link Telegram. Try again.";
};
